import { OrbitControls } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import { useMemo, useState, type ReactNode } from 'react'
import { AmbienteEstudio } from './ambiente/AmbienteEstudio'
import { ControleCamera3D } from './camera/ControleCamera3D'
import {
  DISTANCIA_MINIMA,
  DISTANCIA_MAXIMA,
  alvoCamera,
  posicaoPadrao,
  type ComandoCamera,
  type VolumeEnquadramento,
} from './camera/comandosCamera'
import { ProvedorRotulos } from './componentes/contextoRotulos'

export interface PropsVisualizador3D {
  comandoCamera: ComandoCamera
  /** O que as vistas nomeadas enquadram e o tamanho do rig de luz e sombra. */
  volume: VolumeEnquadramento
  /** Teto do zoom de órbita, em metros. Padrão: quatro vezes o maior lado do volume. */
  distanciaMaxima?: number
  /** Piso do zoom de órbita, em metros. Padrão: 2.4 m (ou 0.6 m com cameraLivre). */
  distanciaMinima?: number
  /** Desliga a órbita enquanto a cena arrasta algo (biruta, caminhão receptor). */
  orbitaHabilitada?: boolean
  /** Órbita vertical completa, aproximação de detalhes e zoom no cursor. */
  cameraLivre?: boolean
  /** Clique no vazio: em geral, limpar a seleção. */
  aoClicarVazio?: () => void
  /** O aluno/professor assumiu a órbita manualmente — cancela qualquer acompanhamento automático da oficina. */
  aoInteragirManual?: () => void
  mostrarRotulos?: boolean
  /** Cor de fundo (céu). */
  corFundo?: string
  /** Neblina [início, fim] em metros, na cor do fundo: esconde a borda do terreno nas cenas abertas. */
  neblinaM?: readonly [number, number]
  children: ReactNode
}

/**
 * Canvas 3D compartilhado por todas as oficinas.
 *
 * Só monta o que é comum — câmera, órbita, luz e mapa de ambiente — e entrega
 * o conteúdo a quem chamou. Cada oficina é dona da sua cena; este componente
 * não conhece caminhão, zona nem pluma.
 */
export function Visualizador3D({
  comandoCamera,
  volume,
  distanciaMaxima,
  distanciaMinima: distanciaMinimaProp,
  orbitaHabilitada = true,
  cameraLivre = false,
  aoClicarVazio,
  aoInteragirManual,
  mostrarRotulos = true,
  corFundo = '#0c1117',
  neblinaM,
  children,
}: PropsVisualizador3D) {
  const maiorLado = Math.max(volume.comprimento, volume.largura, volume.altura)
  // Referência estável: `<OrbitControls target={...}>` reaplica esta prop a
  // cada render, e um array novo a cada chamada desfazia o enquadramento que
  // `ControleCamera3D` acabara de aplicar.
  const alturaAlvo = volume.alturaAlvoM ?? volume.altura * 0.4
  const alvo = useMemo(() => alvoCamera(volume), [volume.centro?.[0], volume.centro?.[1], alturaAlvo])
  const distanciaMinima = distanciaMinimaProp ?? (cameraLivre ? 0.6 : DISTANCIA_MINIMA)
  const limiteCamera = distanciaMaxima ?? DISTANCIA_MAXIMA
  const [posicaoInicial] = useState(() => posicaoPadrao(volume, 1, limiteCamera))
  const alcanceSombra = maiorLado * 0.7

  return (
    <div className="visualizador-3d">
      <Canvas
        camera={{ position: posicaoInicial, fov: 42, near: 0.1, far: Math.max(500, maiorLado * 12) }}
        dpr={[1, 1.8]}
        shadows="percentage"
        // recorte local: o nível de líquido do transbordo é o tanque cortado por um plano horizontal
        gl={{ antialias: true, toneMappingExposure: 1.1, localClippingEnabled: true }}
        onPointerMissed={() => aoClicarVazio?.()}
      >
        <color attach="background" args={[corFundo]} />
        {neblinaM && <fog attach="fog" args={[corFundo, neblinaM[0], neblinaM[1]]} />}
        <ambientLight intensity={0.45} />
        <hemisphereLight args={['#dfe8f2', '#2a2f26', 0.5]} />
        <AmbienteEstudio comprimento={Math.min(maiorLado, 30)} altura={Math.min(volume.altura, 6)} />
        <directionalLight
          position={[maiorLado * 0.4, maiorLado * 0.8, maiorLado * 0.3]}
          intensity={1.8}
          castShadow
          shadow-mapSize={[2048, 2048]}
          shadow-bias={-0.0009}
          shadow-normalBias={0.02}
          shadow-camera-left={-alcanceSombra}
          shadow-camera-right={alcanceSombra}
          shadow-camera-top={alcanceSombra}
          shadow-camera-bottom={-alcanceSombra}
          shadow-camera-near={0.5}
          shadow-camera-far={maiorLado * 3}
        />

        <ProvedorRotulos visiveis={mostrarRotulos}>{children}</ProvedorRotulos>

        <OrbitControls
          makeDefault
          enabled={orbitaHabilitada}
          target={alvo}
          minDistance={distanciaMinima}
          maxDistance={distanciaMaxima ?? maiorLado * 4}
          maxPolarAngle={cameraLivre ? Math.PI : Math.PI / 2 - 0.02}
          enablePan
          screenSpacePanning
          zoomToCursor={cameraLivre}
          enableDamping
        />
        <ControleCamera3D
          comando={comandoCamera}
          volume={volume}
          distanciaMaxima={limiteCamera}
          distanciaMinima={distanciaMinima}
          aoInteragirManual={aoInteragirManual}
        />
      </Canvas>
    </div>
  )
}
